import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Tabs,
  Row,
  Col,
  Spin,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import useGradingSystems from "../../../hooks/academics/useGradingSystems";

const { Title, Text } = Typography;
const { Option } = Select;

const GradingSystemManagement = () => {
  const {
    loading,
    gradingSystems,
    grades,
    calculationResult,
    fetchGradingSystems,
    fetchGrades,
    addGradeToSystem,
    updateGrade,
    deleteGrade,
    calculateGrade,
    setCalculationResult,
  } = useGradingSystems();

  const [selectedSystem, setSelectedSystem] = useState(null);
  const [gradeModalVisible, setGradeModalVisible] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [gradeForm] = Form.useForm();
  const [calcForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("systems");

  useEffect(() => {
    fetchGradingSystems();
  }, []);

  useEffect(() => {
    if (gradingSystems.length > 0 && !selectedSystem) {
      setSelectedSystem(gradingSystems[0]);
    }
  }, [gradingSystems]);

  useEffect(() => {
    if (selectedSystem) {
      fetchGrades(selectedSystem.id);
    }
  }, [selectedSystem]);

  const handleAddGrade = () => {
    if (!selectedSystem) {
      message.warning("Please select a grading system first");
      return;
    }
    setEditingGrade(null);
    gradeForm.resetFields();
    setGradeModalVisible(true);
  };

  const handleEditGrade = (record) => {
    setEditingGrade(record);
    gradeForm.setFieldsValue({
      letter_grade: record.letter_grade,
      numeric_grade: record.numeric_grade,
      min_score: record.min_score,
      max_score: record.max_score,
      remark: record.remark,
    });
    setGradeModalVisible(true);
  };

  const handleGradeSubmit = async () => {
    try {
      const values = await gradeForm.validateFields();
      let result;
      if (editingGrade) {
        result = await updateGrade(editingGrade.id, {
          ...values,
          grading_system: selectedSystem.id,
        });
      } else {
        result = await addGradeToSystem(selectedSystem.id, values);
      }
      if (result) {
        setGradeModalVisible(false);
        gradeForm.resetFields();
        setEditingGrade(null);
        fetchGrades(selectedSystem.id);
        fetchGradingSystems();
      }
    } catch (error) {
      // validation error
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    const success = await deleteGrade(gradeId);
    if (success) {
      fetchGrades(selectedSystem.id);
      fetchGradingSystems();
    }
  };

  const handleCalculate = async () => {
    try {
      const values = await calcForm.validateFields();
      await calculateGrade(values.grading_system, values.score);
    } catch (error) {
      // validation error
    }
  };

  const getGradeColor = (letter) => {
    const colors = {
      A: "green",
      B: "blue",
      C: "gold",
      D: "orange",
      E: "red",
      F: "red",
    };
    return colors[letter?.toUpperCase()] || "default";
  };

  const gradeColumns = [
    {
      title: "Grade",
      dataIndex: "letter_grade",
      key: "letter_grade",
      width: 80,
      render: (text) => (
        <Tag
          color={getGradeColor(text)}
          style={{ fontWeight: 700, fontSize: 14 }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Score Range",
      key: "score_range",
      render: (_, record) => (
        <Text style={{ fontFamily: "monospace" }}>
          {record.min_score} – {record.max_score}
        </Text>
      ),
    },
    {
      title: "Numeric Grade",
      dataIndex: "numeric_grade",
      key: "numeric_grade",
      render: (val) => <Text style={{ fontFamily: "monospace" }}>{val}</Text>,
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render: (text) => <Tag>{text}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditGrade(record)}
          />
          <Popconfirm
            title="Delete this grade?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteGrade(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const systemColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => <Text type="secondary">{text || "—"}</Text>,
    },
    {
      title: "Grade Bands",
      dataIndex: "grades",
      key: "grades",
      render: (grades) => <Tag color="green">{grades?.length || 0} bands</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedSystem(record);
              setActiveTab("grades");
            }}
          >
            View Grades
          </Button>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: "systems",
      label: "Grading Systems",
      children: (
        <Card>
          <Table
            dataSource={gradingSystems}
            columns={systemColumns}
            rowKey="id"
            loading={loading}
            pagination={false}
            rowClassName={(record) =>
              record.id === selectedSystem?.id ? "ant-table-row-selected" : ""
            }
            onRow={(record) => ({
              onClick: () => setSelectedSystem(record),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      ),
    },
    {
      key: "grades",
      label: "Grade Bands",
      children: (
        <Card
          title={
            <Space>
              <span>Grade Bands</span>
              {selectedSystem && <Tag color="green">{selectedSystem.name}</Tag>}
            </Space>
          }
          extra={
            <Space>
              <Select
                style={{ width: 240 }}
                value={selectedSystem?.id}
                onChange={(val) => {
                  const sys = gradingSystems.find((s) => s.id === val);
                  setSelectedSystem(sys);
                }}
                placeholder="Select grading system"
              >
                {gradingSystems.map((sys) => (
                  <Option key={sys.id} value={sys.id}>
                    {sys.name}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddGrade}
                disabled={!selectedSystem}
              >
                Add Grade
              </Button>
            </Space>
          }
        >
          <Table
            dataSource={grades}
            columns={gradeColumns}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </Card>
      ),
    },
    {
      key: "calculator",
      label: "Score Calculator",
      children: (
        <Card title="Score Calculator">
          <Form form={calcForm} layout="inline" onFinish={handleCalculate}>
            <Form.Item
              name="grading_system"
              label="Grading System"
              rules={[{ required: true, message: "Select a system" }]}
            >
              <Select
                style={{ width: 240 }}
                placeholder="Select grading system"
              >
                {gradingSystems.map((sys) => (
                  <Option key={sys.id} value={sys.id}>
                    {sys.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="score"
              label="Score (0–100)"
              rules={[
                { required: true, message: "Enter a score" },
                {
                  type: "number",
                  min: 0,
                  max: 100,
                  message: "Score must be 0–100",
                },
              ]}
            >
              <InputNumber placeholder="e.g. 85" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                htmlType="submit"
                loading={loading}
              >
                Calculate
              </Button>
            </Form.Item>
          </Form>

          {calculationResult && (
            <Card
              style={{ marginTop: 24, maxWidth: 400 }}
              styles={{
                body: { display: "flex", alignItems: "center", gap: 20 },
              }}
            >
              <Tag
                color={getGradeColor(calculationResult.letter_grade)}
                style={{
                  fontSize: 28,
                  fontWeight: 700,
                  padding: "8px 16px",
                  fontFamily: "monospace",
                }}
              >
                {calculationResult.letter_grade}
              </Tag>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                  {calculationResult.remark}
                </div>
                <Text type="secondary" style={{ fontFamily: "monospace" }}>
                  Numeric: {calculationResult.numeric_grade} · Score:{" "}
                  {calculationResult.score}
                </Text>
              </div>
            </Card>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 4 }}>
          Grading Systems
        </Title>
        <Text type="secondary">
          Manage grade bands, calculate scores, and view grading structures
        </Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setCalculationResult(null);
        }}
        items={tabItems}
      />

      <Modal
        title={editingGrade ? "Edit Grade Band" : "Add Grade Band"}
        open={gradeModalVisible}
        onCancel={() => {
          setGradeModalVisible(false);
          gradeForm.resetFields();
          setEditingGrade(null);
        }}
        onOk={handleGradeSubmit}
        okText={editingGrade ? "Update" : "Add"}
        confirmLoading={loading}
      >
        <Form form={gradeForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="letter_grade"
                label="Letter Grade"
                rules={[{ required: true, message: "Enter letter grade" }]}
              >
                <Input placeholder="e.g. A" maxLength={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="numeric_grade"
                label="Numeric Grade"
                rules={[{ required: true, message: "Enter numeric grade" }]}
              >
                <InputNumber placeholder="e.g. 4" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="min_score"
                label="Min Score"
                rules={[
                  { required: true, message: "Enter min score" },
                  { type: "number", min: 0, max: 100, message: "0–100" },
                ]}
              >
                <InputNumber placeholder="e.g. 80" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_score"
                label="Max Score"
                rules={[
                  { required: true, message: "Enter max score" },
                  { type: "number", min: 0, max: 100, message: "0–100" },
                ]}
              >
                <InputNumber placeholder="e.g. 100" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="remark"
            label="Remark"
            rules={[{ required: true, message: "Enter remark" }]}
          >
            <Input placeholder="e.g. Excellent" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GradingSystemManagement;
